import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import MDEditor from "@uiw/react-md-editor";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Info } from "@mui/icons-material";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";

import DocumentLinkAutocomplete from "./document-link-autocomplete";

const schema = z.object({
  doc_version: z.string().refine(
    (x) => {
      return true;
    },
    {
      message: "Invalid doc version",
    }
  ),
});

// type FormValues = z.infer<typeof schema>;

const docOwnerOptions = [
  "Product Management",
  "Marketing",
  "R&D Cybersecurity",
  "RD - Software",
  "EE - General",
  "R&D Mobile Apps",
  "Algorithm",
  "Elect Eng-Software",
  "Elect Eng-Hardware",
  "System Designs Architecture",
  "RD - Quality",
  "Algorithm Engineering",
  "Software - Projects",
  "RD - Directors",
  "Quality Assurance",
  "RD - Design Assurance",
  "RD - Data",
];

const MetadataKB = ({
  open,
  onClose,
  data,
  handleMetadataSubmit,
  firstEdit,
}: any) => {
  const [editDescription, setEditDescription] = useState(false);
  const [description, setDescription] = useState(data?.description || "");
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    // resolver: zodResolver(schema),
    defaultValues: {
      doc_name: data?.doc_name || "",
      doc_id: data?.doc_id || "",
      type: data?.type || "",
      doc_type: data?.doc_type || "",
      upstream: data?.upstream || "",
      downstream: data?.downstream || "",
      keywords: data?.keywords || "",
      description: data?.description || "",
      cplm_status: data?.cplm_status || "",
      doc_version: data?.doc_version || "",
      design_step: data?.design_step || "",
      reviewer_id: data?.reviewer_id || "",
      review_status: data?.review_status || "",
      doc_owner: data?.doc_owner || "",
      product: data?.product || "",
      relationship_type: data?.relationship_type || "",
    },
  });

  const onSubmit = (data: any) => {
    console.log(data);
    handleMetadataSubmit(data);
    onClose();
  };

  React.useEffect(() => {
    setValue("doc_name", data?.doc_name || "");
    setValue("doc_id", data?.doc_id || "");
    setValue("type", data?.type || "");
    setValue("doc_type", data?.doc_type || "");
    setValue("upstream", data?.upstream || "");
    setValue("downstream", data?.downstream || "");
    setValue("keywords", data?.keywords || []);
    setValue("description", data?.description || "");
    setValue("cplm_status", data?.cplm_status || "");
    setValue("doc_version", data?.doc_version || "");
    setValue("design_step", data?.design_step || "");
    setValue("reviewer_id", data?.reviewer_id || "");
    setValue("review_status", data?.review_status || "");
    setValue("doc_owner", data?.doc_owner || "");
    setValue("product", data?.product || "");
    setValue("relationship_type", data?.relationship_type || "");
  }, [data, setValue]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "70rem",
          maxHeight: "90%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "8px",
          overflow: "scroll",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            p: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            textAlign: "center",
            bgcolor: "#d9f7be",
            py: 1,
            borderRadius: "4px",
            mb: 2,
          }}
        >
          <Typography variant="h6">Meta Data</Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" fontWeight={"600"}>
              {" "}
              Document Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              {...register("doc_name", {
                required: "Document Name is required",
              })}
              variant="outlined"
              sx={{ width: "100%" }}
              error={!!errors.doc_name}
              helperText={errors.doc_name?.message as any}
              disabled={
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            />
            <Typography variant="body1" fontWeight={"600"}>
              {" "}
              Document ID <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              {...register("doc_id", {
                required: "Document ID is required",
              })}
              variant="outlined"
              sx={{ width: "100%" }}
              error={!!errors.doc_id}
              helperText={errors.doc_id?.message as any}
              disabled={
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            />
            <Typography variant="body1" fontWeight={"600"}>
              {" "}
              Document Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              {...register("doc_type", {
                required: "Document Type is required",
              })}
              variant="outlined"
              sx={{ width: "100%" }}
              error={!!errors.doc_type}
              helperText={errors.doc_type?.message as any}
              disabled={
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            />

            <Typography variant="body1" fontWeight={"600"}>
              {" "}
              Document Version <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              {...register("doc_version", {
                required: "Document Version is required",
              })}
              variant="outlined"
              sx={{ width: "100%" }}
              error={!!errors.doc_version}
              helperText={errors.doc_version?.message as any}
              disabled={
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            />

            <Typography variant="body1" fontWeight={"600"}>
              {" "}
              Design Step <span style={{ color: "red" }}>*</span>
            </Typography>
            <Controller
              name="design_step"
              control={control}
              rules={{ required: "Design Step is required" }}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="design_step_label"
                  label="design_step"
                  error={!!errors.design_step}
                >
                  {[
                    "Technical Design Review Document",
                    "Design Verification",
                    "UI Design",
                    "Design Change",
                    "Design Input",
                    "Standards",
                    "Architecture",
                    "User Guide",
                    "Design Output",
                    "Risk",
                    "Risk Management",
                    "Other",
                  ]?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              )}
              disabled={
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              mt: 2,
              margin: "1rem 0  0.5rem 0",
            }}
          >
            <Typography
              variant="body1"
              fontWeight={"600"}
              sx={{ margin: "0 6rem 0 0" }}
            >
              Product
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl
              fullWidth
              error={!!errors.product}
              sx={{ maxWidth: "65%" }}
            >
              <InputLabel id="product-label">Product</InputLabel>
              <Controller
                name="product"
                control={control}
                defaultValue=""
                rules={{ required: "Please select a product" }}
                render={({ field }) => (
                  <Select {...field} labelId="product-label" label="Product">
                    <MenuItem value="Dexcom G7">Dexcom G7</MenuItem>
                    <MenuItem value="Dexcom Stelo">Dexcom Stelo</MenuItem>
                    <MenuItem value="Dexcom Proton">Dexcom Proton</MenuItem>
                  </Select>
                )}
                disabled={
                  data?.processing_status === "Inprogress" ||
                  data?.processing_status === "Failed"
                }
              />
            </FormControl>
          </Box>

          {/* Upstream & Downstream */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              mt: 2,
            }}
          >
            <Typography
              variant="body1"
              fontWeight={"600"}
              sx={{ whiteSpace: "nowrap", margin: "0 1rem 0 0" }}
            >
              Upstream Document{" "}
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info />
                </HoverCardTrigger>
                <HoverCardContent className="w-80 break-words whitespace-normal">
                  <div className="flex justify-between space-x-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">Info</h4>
                      <p className="text-sm">
                         Upstream documents are parent documents for the current document
                      </p>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </Typography>

            <DocumentLinkAutocomplete
              title="Upstream document"
              field="upstream"
              value={data?.upstream || ""}
              setValue={(field: any, newValue: any) => {
                setValue(field, newValue);
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              mt: 2,
              margin: "1rem 0 0.5rem 0",
            }}
          >
            <Typography
              variant="body1"
              fontWeight={"600"}
              sx={{ margin: "0 1rem 0 0", whiteSpace: "nowrap" }}
            >
              Downstream Document
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info />
                </HoverCardTrigger>
                <HoverCardContent className="w-80 break-words whitespace-normal">
                  <div className="flex justify-between space-x-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">Info</h4>
                      <p className="text-sm">
                        Downstream documents are children documents for the current document
                      </p>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </Typography>

            <DocumentLinkAutocomplete
              title={"Downstream document"}
              field={"downstream"}
              value={data?.downstream}
              setValue={(field: any, newValue: any) => {
                setValue(field, newValue);
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              mt: 2,
              margin: "1rem 0 0.5rem 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                mt: 2,
                margin: "1rem 0 0.5rem 0",

                whiteSpace: "nowrap",
              }}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Document Owner
              </Typography>
              <FormControl
                fullWidth
                error={!!errors.downstream}
                sx={{ minWidth: "10rem" }}
              >
                <InputLabel id="doc_owner-label">Document Owner</InputLabel>
                <Controller
                  name="doc_owner"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="doc_owner-label"
                      label="doc_owner"
                    >
                      {docOwnerOptions?.map((option) => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  )}
                  disabled={
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                mt: 2,
                margin: "1rem 0 0.5rem 0",

                whiteSpace: "nowrap",
              }}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Relationship Type
              </Typography>
              <FormControl
                fullWidth
                error={!!errors.relationship_type}
                sx={{ minWidth: "10rem" }}
              >
                <InputLabel id="relationship_type">
                  Relationship Type
                </InputLabel>
                <Controller
                  name="relationship_type"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="relationship-type-label"
                      label="relationship_type"
                    >
                      <MenuItem value="VERIFIED_BY">VERIFIED BY</MenuItem>
                      <MenuItem value="VALIDATED_BY">VALIDATED BY</MenuItem>
                      <MenuItem value="RELATED_TO">RELATED TO</MenuItem>
                      <MenuItem value="SUPPORTS">SUPPORTS</MenuItem>
                      <MenuItem value="REFERENCES">REFERENCES</MenuItem>
                      <MenuItem value="CONTRADICTS">CONTRADICTS</MenuItem>
                    </Select>
                  )}
                  disabled={
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                variant="body1"
                fontWeight={"600"}
                sx={{ whiteSpace: "nowrap", margin: "0 1rem 0 0" }}
              >
                CPLM Status
              </Typography>
              <FormControl
                fullWidth
                error={!!errors.cplm_status}
                sx={{ minWidth: "12rem" }}
              >
                <InputLabel id="cplm_status-label">CPLM Status</InputLabel>
                <Controller
                  name="cplm_status"
                  control={control}
                  defaultValue=""
                  // rules={{ required: "cplm_status is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="cplm_status-label"
                      label="CPLM Status"
                    >
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="Pending Approval">
                        Pending Approval
                      </MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Unknown">Unknown</MenuItem>
                    </Select>
                  )}
                  disabled={
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                />
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "1rem",
              mt: 2,
              margin: "1rem 0 0.5rem 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                gap: "1rem",
                mt: 2,
                margin: "1rem 0 0.5rem 0",
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                variant="body1"
                fontWeight={"600"}
                sx={{ margin: "0 2.5rem 0 0", whiteSpace: "nowrap" }}
              >
                {" "}
                Reviewer Id
              </Typography>
              <TextField
                {...register("reviewer_id")}
                variant="outlined"
                sx={{ minWidth: "100%", alignSelf: "flex-start" }}
                error={!!errors.reviewer_id}
                helperText={errors.reviewer_id?.message as any}
                disabled={
                  data?.processing_status === "Inprogress" ||
                  data?.processing_status === "Failed"
                }
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                mt: 2,
                margin: "1rem 0 0.5rem 10rem",
                whiteSpace: "nowrap",
              }}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Review Status
              </Typography>
              <FormControl
                fullWidth
                error={!!errors.review_status}
                sx={{ minWidth: "100%" }}
              >
                <InputLabel id="review-status-label">Review Status</InputLabel>
                <Controller
                  name="review_status"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="review-status-label"
                      label="review_status"
                    >
                      <MenuItem value="UNREVIEWED">UNREVIEWED</MenuItem>
                      <MenuItem value="REVIEWED">REVIEWED</MenuItem>
                      <MenuItem value="VALIDATED">VALIDATED</MenuItem>
                    </Select>
                  )}
                  disabled={
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                />
              </FormControl>
            </Box>
          </Box>

          {/* Keywords Field (Disabled when firstEdit is true) */}
          {!firstEdit && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" fontWeight={"600"}>
                Keywords
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Info />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-40">
                    <div className="flex justify-between space-x-4">
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold">Info</h4>
                        <p className="text-sm">Keywords for this document</p>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </Typography>
              <TextField
                {...register("keywords")}
                variant="outlined"
                multiline
                rows={3}
                sx={{ width: "100%" }}
                disabled={
                  data?.processing_status === "Inprogress" ||
                  data?.processing_status === "Failed"
                }
              />
            </Box>
          )}

          {/* Description Field (Disabled when firstEdit is true) */}
          {!firstEdit && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" fontWeight={"600"}>
                Description
              </Typography>
              <Box sx={{ position: "relative", width: "100%" }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    textTransform: "none",
                    fontSize: "12px",
                    padding: "2px 6px",
                    minWidth: "auto",
                    height: "20px",
                    lineHeight: "1",
                  }}
                  disabled={
                    !firstEdit ||
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                >
                  Edit Description
                </Button>

                <TextField
                  {...register("description")}
                  variant="outlined"
                  multiline
                  rows={3}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root": {
                      paddingTop: "30px",
                    },
                  }}
                  disabled={
                    firstEdit ||
                    data?.processing_status === "Inprogress" ||
                    data?.processing_status === "Failed"
                  }
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              justifyContent: "flex-end",
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{ backgroundColor: "#2196f3" }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              sx={{}}
              disabled={
                !isValid ||
                data?.processing_status === "Inprogress" ||
                data?.processing_status === "Failed"
              }
            >
              Confirm
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default MetadataKB;
